import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Cmd/Ctrl+K palette for dashboard navigation.
 *
 * The command list is curated rather than derived from the router so the
 * palette stays focused on the destinations owners actually navigate to.
 * Search ranks by case-insensitive substring + group prefix.
 */
const COMMANDS = [
  { group: 'Brand',     label: 'Brand overview',  path: '/dashboard/brand/overview' },
  { group: 'Brand',     label: 'Brand settings',  path: '/dashboard/brand/settings' },
  { group: 'Brand',     label: 'Domains (DNS)',   path: '/dashboard/brand/domains' },
  { group: 'Brand',     label: 'IAM',             path: '/dashboard/brand/iam' },
  { group: 'Brand',     label: 'Billing',         path: '/dashboard/brand/billing' },
  { group: 'Brand',     label: 'API keys',        path: '/dashboard/brand/api-keys' },
  { group: 'Brand',     label: 'Webhooks',        path: '/dashboard/brand/webhooks' },
  { group: 'Brand',     label: 'Notifications',   path: '/dashboard/brand/notifications' },
  { group: 'Brand',     label: 'Activity log',    path: '/dashboard/brand/activity' },

  { group: 'Ecommerce', label: 'Commerce',        path: '/dashboard/ecommerce/commerce' },
  { group: 'Ecommerce', label: 'Orders',          path: '/dashboard/ecommerce/orders' },
  { group: 'Ecommerce', label: 'Audience',        path: '/dashboard/ecommerce/audience' },
  { group: 'Ecommerce', label: 'Promotions',      path: '/dashboard/ecommerce/promotions' },
  { group: 'Ecommerce', label: 'Reviews',         path: '/dashboard/ecommerce/reviews' },
  { group: 'Ecommerce', label: 'Low stock',       path: '/dashboard/ecommerce/low-stock' },
  { group: 'Ecommerce', label: 'Contact inbox',   path: '/dashboard/ecommerce/inbox' },
  { group: 'Ecommerce', label: 'Marketing',       path: '/dashboard/ecommerce/marketing' },

  { group: 'NFT',       label: 'NFT Studio',      path: '/dashboard/nft/studio' },

  { group: 'Mail',      label: 'Mail manager',    path: '/dashboard/mail/manager' },

  { group: 'Accounting', label: 'Accounting',     path: '/dashboard/accounting/home' },

  { group: 'Site',      label: 'Pages',           path: '/dashboard/webpage/pages' },
];

function scoreCommand(cmd, q) {
  if (!q) return 0;
  const hay = `${cmd.group} ${cmd.label}`.toLowerCase();
  const needle = q.toLowerCase();
  if (!hay.includes(needle)) return -1;
  // Prefer matches at the start of label, then group.
  const li = cmd.label.toLowerCase().indexOf(needle);
  if (li === 0) return 100;
  const gi = cmd.group.toLowerCase().indexOf(needle);
  if (gi === 0) return 50;
  return 10;
}

// Inline <Kbd> equivalent — MUI doesn't ship one out of the box, so we
// render a styled `<kbd>` that picks up the theme's divider color.
function Kbd({ children }) {
  return (
    <Box
      component='kbd'
      sx={{
        fontFamily: 'inherit',
        fontSize: '0.7rem',
        px: '6px', py: '1px',
        borderRadius: '4px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        color: 'text.secondary',
      }}
    >
      {children}
    </Box>
  );
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global keyboard shortcut: Cmd/Ctrl+K (Esc + click handled by Dialog).
  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (open) {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => i + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => { if (!open) { setQuery(''); setActiveIdx(0); } }, [open]);

  const results = useMemo(() => {
    if (!query.trim()) return COMMANDS;
    return COMMANDS
      .map((c) => ({ c, s: scoreCommand(c, query.trim()) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.c);
  }, [query]);

  useEffect(() => { setActiveIdx(0); }, [results.length, query]);

  const go = (cmd) => {
    if (!cmd) return;
    setOpen(false);
    navigate(cmd.path);
  };

  const safeIdx = results.length === 0 ? -1 : Math.min(activeIdx, results.length - 1);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: '10px 14px', borderBottom: '1px solid', borderColor: 'divider' }}>
          <InputBase
            inputRef={inputRef}
            placeholder='Jump to…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                go(results[safeIdx]);
              }
            }}
            autoFocus
            fullWidth
            sx={{ fontSize: '1rem' }}
          />
        </Box>
        <Box sx={{ maxHeight: 420, overflowY: 'auto', py: '6px' }}>
          {results.length === 0 && (
            <Box p='14px'>
              <Typography variant='body2' color='text.secondary'>
                No matches for "{query}".
              </Typography>
            </Box>
          )}
          {results.map((c, i) => (
            <Stack
              key={c.path}
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{
                px: '14px',
                py: '8px',
                cursor: 'pointer',
                bgcolor: i === safeIdx ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => go(c)}
            >
              <Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  textTransform='uppercase'
                  fontWeight='700'
                  display='block'
                >
                  {c.group}
                </Typography>
                <Typography variant='body2' fontWeight='600'>{c.label}</Typography>
              </Box>
              <Typography variant='caption' color='text.secondary'>{c.path}</Typography>
            </Stack>
          ))}
        </Box>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{
            px: '14px',
            py: '8px',
            borderTop: '1px solid',
            borderColor: 'divider',
            fontSize: '0.75rem',
            color: 'text.secondary',
          }}
        >
          <Stack direction='row' spacing='12px'>
            <Stack direction='row' alignItems='center' spacing='4px'>
              <Kbd>↑</Kbd><Kbd>↓</Kbd>
              <Typography variant='caption'>navigate</Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing='4px'>
              <Kbd>⏎</Kbd>
              <Typography variant='caption'>open</Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing='4px'>
              <Kbd>esc</Kbd>
              <Typography variant='caption'>close</Typography>
            </Stack>
          </Stack>
          <Stack direction='row' alignItems='center' spacing='4px'>
            <Kbd>⌘</Kbd><Kbd>K</Kbd>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
